import 'twin.macro';
import { Chips } from 'components/Chips';
import React, { useState } from 'react';
import SimpleNarrowPage from '@teetsh/app/src/components/Layouts/SimpleNarrowPage';
import useEDTTemplates from './useEDTTemplates';
import { StrapiEDT, StrapiEDTNiveau } from './types';
import { orderByNiveau } from '@teetsh/app/src/utils/orderByNiveau';
import { PreviewModal } from './PreviewModal';
import { ClassroomsMappingModal } from './ClassroomsMappingModal';
import ErrorFallback from '@teetsh/app/src/components/Errors/ErrorFallback';
import { useTranslation } from 'react-i18next';
import TemplateCard, { TagData } from 'components/TemplateCard';
import { useSchoolGoTo } from 'containers/App/useSchoolGoTo';
import { OnboardingModulePageWrapper } from '@teetsh/app/src/components/OnboardingModulePageWrapper';
import { BadgeColor } from 'types/colors';
import { useUser } from 'containers/App/context/useUser';

function getAllUniqNiveaux(templates: StrapiEDT[]): StrapiEDTNiveau[] {
  const uniqueNiveaux = new Set();
  const result: StrapiEDTNiveau[] = [];

  for (const template of templates) {
    for (const niveau of template.niveaux) {
      if (!uniqueNiveaux.has(niveau.name)) {
        uniqueNiveaux.add(niveau.name);
        result.push(niveau);
      }
    }
  }

  return result;
}

export function TemplatePicker() {
  const { scholarSystem } = useUser();

  const { isLoading, data, error } = useEDTTemplates({
    scholarSystem,
  });
  const { t } = useTranslation(['emploiDuTemps', 'common']);
  const [filters, setFilters] = useState<string[]>([]);
  const [isClassroomMappingModalOpen, setIsClassroomMappingModalOpen] =
    useState<StrapiEDT | undefined>(undefined);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const edts: StrapiEDT[] =
    filters.length > 0
      ? (data || []).filter(
          edt =>
            !!edt.niveaux.find(c => filters.includes(c.name)) ||
            (filters.includes('editor') && edt.editor?.name),
        )
      : data || [];

  const allNiveaux = orderByNiveau(getAllUniqNiveaux(data || []), c => c.name);
  const goTo = useSchoolGoTo();
  if (isLoading) {
    return (
      <OnboardingModulePageWrapper
        title={t('emploiDuTemps:new.templateTitle')}
        onBack={() => goTo('emploi-du-temps/new')}
      >
        <SimpleNarrowPage width="5xl">
          <div tw="mt-16 mx-auto max-w-5xl grid items-center justify-center md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="tsh-skeleton" tw="aspect-square" />
            <div className="tsh-skeleton" tw="aspect-square" />
            <div className="tsh-skeleton" tw="aspect-square" />
          </div>
        </SimpleNarrowPage>
      </OnboardingModulePageWrapper>
    );
  }
  if (error) {
    return (
      <ErrorFallback
        error={error as any}
        subject="la récupération des modèles"
      />
    );
  }

  return (
    <OnboardingModulePageWrapper
      title={t('emploiDuTemps:new.templateTitle')}
      onBack={() => goTo('emploi-du-temps/new')}
    >
      <SimpleNarrowPage width="5xl">
        <PreviewModal
          url={previewUrl}
          onClose={() => setPreviewUrl(undefined)}
        />
        <ClassroomsMappingModal
          strapiEdt={isClassroomMappingModalOpen}
          onClose={() => setIsClassroomMappingModalOpen(undefined)}
        />
        <>
          <h3 tw="text-base font-semibold tracking-wide text-neutral-quiet mb-2">
            {t('common:filterBar.title')}
          </h3>
          <div tw="mb-4 lg:mb-6">
            <Chips
              value={filters}
              onChange={setFilters}
              options={[
                ...allNiveaux.map(c => ({
                  value: c.name,
                  label: c.name,
                })),
                ...(edts.some(e => e.editor?.name)
                  ? [
                      {
                        value: 'editor',
                        label: t('common:editorTag'),
                      },
                    ]
                  : []),
              ]}
            />
          </div>
        </>
        <div tw="grid grid-cols-1 gap-6 mb-8 sm:grid-cols-2 lg:grid-cols-3">
          {edts.map(edt => {
            const tags: TagData[] = [
              ...edt.categories.map(cat => ({
                key: cat.name,
                label: cat.name,
                color: cat.color,
              })),
              ...(edt.editor?.name
                ? [
                    {
                      key: 'editor',
                      label: t('common:editorTag'),
                      color: 'indigo' as BadgeColor,
                    },
                  ]
                : []),
            ];

            return (
              <TemplateCard
                key={edt.documentId}
                item={edt}
                tags={tags}
                onPreview={() => setPreviewUrl(edt.preview)}
                onSelect={() => setIsClassroomMappingModalOpen(edt)}
              />
            );
          })}
        </div>
      </SimpleNarrowPage>
    </OnboardingModulePageWrapper>
  );
}
